import { ExtendedAllergy } from '@seen/core/models/allergies'
import type { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { IconPrinter } from '@tabler/icons-react'
import { type JSX, useCallback, useMemo, useRef } from 'react'
import { FacesheetCardActionButton } from '#src/components/patient/detail-content/facesheet/FacesheetCardActionButton.tsx'
import { useParallelSearchOne } from '#src/hooks/useParallelSearchOne.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

interface PrintMealTicketProps {
  participant: Participant
  isDisabled?: boolean
}

export function PrintMealTicket({
  participant,
  isDisabled = false,
}: PrintMealTicketProps): JSX.Element {
  const printFrameRef = useRef<HTMLIFrameElement>(null)

  // Fetch diet questionnaire response
  const dietQuestionnaireQuery = useMemo(
    () => [
      {
        resourceClass: ExtendedQuestionnaireResponse,
        query: ExtendedQuestionnaireResponse.searchQuery({
          questionnaire: questionnaires.DietQuestionnaire,
          subject: { reference: `Patient/${participant.id}` },
        }),
      },
    ],
    [participant.id]
  )

  const [dietQuestionnaireResult] = useParallelSearchOne(
    dietQuestionnaireQuery,
    {
      query: {
        enabled: true,
      },
    }
  )

  // Fetch allergies
  const { data: allergies } = useSearch(ExtendedAllergy, {
    patient: participant.reference,
    'clinical-status': 'active',
  })

  const handlePrint = useCallback((): void => {
    const dietResponse = dietQuestionnaireResult.data
    if (!dietResponse) {
      return
    }

    const diet = dietResponse.getAnswerValue('diet')
    const fluidConsistency = dietResponse.getAnswerValue('fluid-consistency')
    const restrictions = dietResponse.getAnswerValue('fluid-restrictions')
    const supplements = dietResponse.getAnswerValue('supplements')
    const dislikes = dietResponse.getAnswerValue('dislikes')
    const notes = dietResponse.getAnswerValue('notes')

    // Convert values to strings and handle null/undefined
    const dietStr = diet ? String(diet) : ''
    const fluidConsistencyStr = fluidConsistency ? String(fluidConsistency) : ''
    const restrictionsStr = restrictions ? String(restrictions) : ''
    const supplementsStr = supplements ? String(supplements) : ''
    const dislikesStr = dislikes ? String(dislikes) : ''
    const notesStr = notes ? String(notes) : ''

    const ticketContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
        <title>Meal Ticket - ${participant.name}</title>
        <style>
          /* Import shared styles */
          @import url('/styles.css');
          
          /* Embed web-safe fonts with Chinese support */
          @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;700&display=swap');
          
          body {
            font-family: 'Noto Sans SC', 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', sans-serif;
            padding: 20px;
            max-width: 400px;
            margin: 0 auto;
            font-size: 12pt;
            background-color: white;
            color: black;
          }
          h1 {
            font-size: 18pt;
            margin-bottom: 10px;
            font-weight: 700;
          }
          .participant-info {
            margin-bottom: 15px;
            font-size: 14pt;
          }
          .section {
            border: 1px solid hsl(var(--border));
            padding: 10px;
            border-radius: var(--radius);
            margin-bottom: 15px;
          }
          .section-title {
            font-size: 14pt;
            margin-top: 0;
            margin-bottom: 10px;
            color: hsl(var(--foreground));
            font-weight: 700;
          }
          .item {
            margin-bottom: 8px;
          }
          .label {
            font-weight: bold;
          }
          .timestamp {
            font-size: 10pt;
            color: hsl(var(--foreground-tertiary));
          }
          .allergy-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .allergy-item {
            padding: 4px 0;
            border-bottom: 1px solid hsl(var(--border));
          }
          .allergy-item:last-child {
            border-bottom: none;
          }
          /* Ensure Chinese character display */
          .zh {
            font-family: 'Noto Sans SC', 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', sans-serif;
            display: inline-block;
          }
          
          /* Single font-family rule that includes both English and Chinese fonts */
          :lang(zh), .zh {
            font-family: 'Noto Sans SC', 'Microsoft YaHei', 'SimHei', 'Arial Unicode MS', sans-serif;
          }
          
          @media print {
            * {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
            }
          }
        </style>
      </head>
      <body>
        <h1>Seen Health SGV <span lang="zh">餐票</span></h1>
        <div class="participant-info">
          <div><b>${participant.name}</b></div>
        </div>
        <div class="section">
          <h2 class="section-title">Allergies <span class="zh">&#36807;&#25935;</span></h2>
          ${
            allergies && allergies.length > 0
              ? `
            <ul class="allergy-list">
              ${allergies
                .map(
                  (allergy) => `
                <li class="allergy-item">${allergy.name}</li>
              `
                )
                .join('')}
            </ul>
          `
              : '<div>No active allergies listed <span class="zh">&#27809;&#26377;&#24050;&#30693;&#36807;&#25935;</span></div>'
          }
        </div>
        <div class="section">
          <h2 class="section-title">Dietary Needs <span lang="zh">飲食需求</span></h2>
          ${dietStr ? `<div class="item"><span class="label">Diet <span lang="zh">飲食</span>:</span> ${dietStr}</div>` : ''}
          ${fluidConsistencyStr ? `<div class="item"><span class="label">Fluid Consistency <span lang="zh">流体稠度</span>:</span> ${fluidConsistencyStr}</div>` : ''}
          ${restrictionsStr && restrictionsStr.toLowerCase() !== 'none' ? `<div class="item"><span class="label">Restrictions <span lang="zh">限制</span>:</span> ${restrictionsStr}</div>` : ''}
          ${supplementsStr && supplementsStr.toLowerCase() !== 'none' ? `<div class="item"><span class="label">Supplements <span lang="zh">补充</span>:</span> ${supplementsStr}</div>` : ''}
        </div>
        ${
          dislikesStr
            ? `
        <div class="section">
          <h2 class="section-title">Dislikes <span lang="zh">不喜欢的食物</span></h2>
          <div class="item">${dislikesStr}</div>
        </div>
        `
            : ''
        }
        ${
          notesStr
            ? `
        <div class="section">
          <h2 class="section-title">Notes <span lang="zh">备注</span></h2>
          <div class="item">${notesStr}</div>
        </div>
        `
            : ''
        }
        <div class="timestamp">
          Printed on: ${new Date().toLocaleString()} <span class="zh">&#25171;&#21360;&#26102;&#38388;</span>
        </div>
      </body>
      </html>
    `

    const iframe = printFrameRef.current
    if (!iframe) {
      return
    }

    const iframeDoc = iframe.contentDocument
    if (!iframeDoc) {
      return
    }

    iframeDoc.open()
    iframeDoc.write(ticketContent)
    iframeDoc.close()

    setTimeout(() => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    }, 250)
  }, [participant, dietQuestionnaireResult.data, allergies])

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    e.preventDefault()
    handlePrint()
  }

  return (
    <>
      <FacesheetCardActionButton
        onClick={handleClick}
        aria-label="Print meal ticket"
        title="Print Meal Ticket"
        disabled={isDisabled}
      >
        <IconPrinter />
      </FacesheetCardActionButton>
      <iframe
        ref={printFrameRef}
        style={{ display: 'none', height: 0, width: 0 }}
        title="Print Frame"
      />
    </>
  )
}
